/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, OnInit, inject, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { NotificationService } from '@alfresco/adf-core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MigrationService } from './migration.service';
import { MappingRow, MigrationJob } from './migration.models';

/**
 * Mapping editor for a saved migration draft. Pick a job, edit its five
 * mapping tables (folders / content types / metadata / users / permissions),
 * save — the rows live inside the job's own JSON document.
 */
@Component({
  selector: 'app-mig-mapping',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './migration-mapping.component.html'
})
export class MigrationMappingComponent implements OnInit {
  private readonly service = inject(MigrationService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);

  readonly jobs = signal<MigrationJob[]>([]);
  readonly selectedId = signal('');
  readonly busy = signal(false);

  job: MigrationJob | null = null;
  mapTable: 'folders' | 'types' | 'metadata' | 'users' | 'permissions' = 'metadata';
  newMapSource = '';
  newMapTarget = '';
  newMapDefault = '';

  readonly tables = ['folders', 'types', 'metadata', 'users', 'permissions'] as const;

  ngOnInit(): void {
    this.service.jobs().subscribe((list) => {
      this.jobs.set(list);
      const first = list.find((j) => j.status === 'draft') ?? list[0];
      if (first) {
        this.pick(first.id as string);
      }
    });
  }

  pick(id: string): void {
    this.selectedId.set(id);
    const found = this.jobs().find((j) => j.id === id) ?? null;
    this.job = found
      ? {
          ...found,
          mapping: {
            folders: [...(found.mapping?.folders ?? [])],
            types: [...(found.mapping?.types ?? [])],
            metadata: [...(found.mapping?.metadata ?? [])],
            users: [...(found.mapping?.users ?? [])],
            permissions: [...(found.mapping?.permissions ?? [])]
          }
        }
      : null;
  }

  addRow(): void {
    if (!this.job || !this.newMapSource.trim()) {
      return;
    }
    const row: MappingRow = {
      source: this.newMapSource.trim(),
      target: this.newMapTarget.trim(),
      action: 'map',
      defaultValue: this.newMapDefault.trim() || undefined
    };
    this.job.mapping[this.mapTable] = [...this.job.mapping[this.mapTable], row];
    this.newMapSource = '';
    this.newMapTarget = '';
    this.newMapDefault = '';
  }

  removeRow(index: number): void {
    if (this.job) {
      this.job.mapping[this.mapTable] = this.job.mapping[this.mapTable].filter((_, i) => i !== index);
    }
  }

  save(): void {
    if (!this.job) {
      return;
    }
    this.busy.set(true);
    this.service.saveJob(this.job).subscribe({
      next: () => {
        this.busy.set(false);
        this.notification.showInfo(this.translate.instant('DMS.MIGRATION.MAP_SAVED'));
      },
      error: () => {
        this.busy.set(false);
        this.notification.showError(this.translate.instant('DMS.MIGRATION.SAVE_FAIL'));
      }
    });
  }

  trackByRow(i: number): number {
    return i;
  }
}
